import type { ContactImportSchema } from "./useContactImport";

// Reads a .vcf file into the rows the contact import already takes, so a vCard
// goes through the same company matching and duplicate checks as a CSV row.
// LCCRM-87.
//
// Written against what address books actually export rather than the whole of
// RFC 2426/6350: Apple Contacts (grouped `item1.EMAIL` properties, escaped
// colons in URLs, X-SOCIALPROFILE), Google Contacts and Outlook (vCard 2.1 with
// quoted-printable values). Photos are skipped: the CRM derives its own avatars.

type Property = {
  name: string;
  params: Record<string, string[]>;
  value: string;
};

/** Every contact in the file, in file order. A file with no card gives []. */
export const parseVCards = (text: string): ContactImportSchema[] =>
  splitCards(unfold(text)).map(toContact);

// A long value continues on the next line: after a leading space or tab
// (RFC folding), or after a trailing "=" in a quoted-printable value (2.1).
const unfold = (text: string): string[] => {
  const lines: string[] = [];
  for (const raw of text.replace(/\r\n?/g, "\n").split("\n")) {
    const previous = lines[lines.length - 1];
    if (previous !== undefined && /^[ \t]/.test(raw)) {
      lines[lines.length - 1] = previous + raw.slice(1);
    } else if (
      previous !== undefined &&
      previous.endsWith("=") &&
      /ENCODING=QUOTED-PRINTABLE/i.test(previous.split(":")[0])
    ) {
      lines[lines.length - 1] = previous.slice(0, -1) + raw;
    } else {
      lines.push(raw);
    }
  }
  return lines;
};

const splitCards = (lines: string[]): Property[][] => {
  const cards: Property[][] = [];
  let current: Property[] | null = null;
  for (const line of lines) {
    if (/^BEGIN:VCARD\s*$/i.test(line)) {
      current = [];
    } else if (/^END:VCARD\s*$/i.test(line)) {
      if (current) cards.push(current);
      current = null;
    } else if (current) {
      const property = parseLine(line);
      if (property) current.push(property);
    }
  }
  return cards;
};

const parseLine = (line: string): Property | null => {
  const colon = line.indexOf(":");
  if (colon <= 0) return null;
  const [rawName, ...rawParams] = line.slice(0, colon).split(";");
  // `item1.EMAIL` is Apple's way of tying a label to a property; the group
  // carries no meaning of its own here.
  const name = rawName.split(".").pop()!.toUpperCase();
  const params: Record<string, string[]> = {};
  for (const param of rawParams) {
    const eq = param.indexOf("=");
    // 2.1 writes bare types: `TEL;WORK;VOICE:`.
    const key = eq === -1 ? "TYPE" : param.slice(0, eq).toUpperCase();
    const values = (eq === -1 ? param : param.slice(eq + 1))
      .replace(/"/g, "")
      .split(",")
      .map((value) => value.trim().toUpperCase())
      .filter(Boolean);
    params[key] = [...(params[key] ?? []), ...values];
  }
  let value = line.slice(colon + 1);
  if (params.ENCODING?.includes("QUOTED-PRINTABLE")) {
    value = decodeQuotedPrintable(value);
  }
  return { name, params, value };
};

const decodeQuotedPrintable = (value: string): string => {
  const bytes: number[] = [];
  for (let i = 0; i < value.length; i += 1) {
    const hex = value.slice(i + 1, i + 3);
    if (value[i] === "=" && /^[0-9A-F]{2}$/i.test(hex)) {
      bytes.push(parseInt(hex, 16));
      i += 2;
    } else {
      // Anything outside an escape is ASCII in a quoted-printable value.
      bytes.push(value.charCodeAt(i) & 0xff);
    }
  }
  return new TextDecoder("utf-8").decode(new Uint8Array(bytes));
};

// \n is a line break; \, \; \: and \\ are the character itself.
const unescape = (value: string): string =>
  value.replace(/\\([nN,;:\\])/g, (_, char: string) =>
    char === "n" || char === "N" ? "\n" : char,
  );

// The parts of a structured value (N, ORG): split on ";" unless it is escaped.
const components = (value: string): string[] =>
  value.split(/(?<!\\);/).map((part) => unescape(part).trim());

const isPreferred = (property: Property) =>
  property.params.TYPE?.includes("PREF") || property.params.PREF !== undefined;

// The one to keep when a card has several: the one marked preferred, else the
// first. The rest are kept in the background text rather than dropped.
const pick = (properties: Property[]): [string, string[]] => {
  const values = properties
    .map((property) => ({
      value: unescape(property.value).trim(),
      preferred: isPreferred(property),
    }))
    .filter(({ value }) => value !== "");
  const chosen = values.find(({ preferred }) => preferred) ?? values[0];
  if (!chosen) return ["", []];
  return [
    chosen.value,
    [...new Set(values.map(({ value }) => value))].filter(
      (value) => value !== chosen.value,
    ),
  ];
};

const toContact = (card: Property[]): ContactImportSchema => {
  const all = (name: string) =>
    card.filter((property) => property.name === name);
  const first = (name: string) => all(name)[0];

  let firstName = "";
  let lastName = "";
  const n = first("N");
  if (n) {
    const [family = "", given = "", additional = ""] = components(n.value);
    firstName = [given, additional].filter(Boolean).join(" ");
    lastName = family;
  }
  if (!firstName && !lastName) {
    // No structured name: take the last word of the display name as the
    // family name, which is right far more often than not.
    const words = unescape(first("FN")?.value ?? "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    lastName = words.length > 1 ? words.pop()! : "";
    firstName = words.join(" ");
  }

  const [email, otherEmails] = pick(all("EMAIL"));
  const [phone, otherPhones] = pick(all("TEL"));

  const links = [...all("URL"), ...all("X-SOCIALPROFILE")]
    .map((property) => unescape(property.value).trim())
    // Apple writes x-apple: handles for some profiles; only web links count.
    .filter((url) => /^https?:\/\//i.test(url));
  const linkedin = links.find((url) => /(^|\.|\/\/)linkedin\.com\//i.test(url));

  const background = [
    unescape(first("NOTE")?.value ?? "").trim(),
    otherEmails.length > 0 ? `Other emails: ${otherEmails.join(", ")}` : "",
    otherPhones.length > 0 ? `Other phones: ${otherPhones.join(", ")}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  return {
    first_name: firstName,
    last_name: lastName,
    gender: "",
    title: unescape(first("TITLE")?.value ?? "").trim(),
    // ORG is "company;department;..."; the company is the first part.
    company: components(first("ORG")?.value ?? "")[0] ?? "",
    email,
    phone,
    background,
    avatar: "",
    first_seen: "",
    last_seen: "",
    status: "",
    tags: "",
    linkedin_url: linkedin ?? "",
  };
};
